import * as Sentry from '@sentry/react';
import { ApisauceInstance } from 'apisauce';

import getData from '@nw/api';
import {
  API_FAILED,
  API_FETCHING,
  API_INITIAL,
  API_SUCCESS
} from '@nw/api-constants';
import { apiMethods, Config } from '@nw/shared/config';
import { getUserInfoDetails, resStatuses, statusCodes } from '@nw/shared/utils';

function isAccessTokenExpiredMessage(message: string) {
  const errorResponse = JSON.parse(message);
  let isAccessTokenExpired = false;
  try {
    const { status, data } = errorResponse;
    const failureMessage = data?.detail;
    const resStatus = data?.res_status;
    // infrastructure errors cases
    Sentry.addBreadcrumb({
      type: 'info',
      category: 'api.utils',
      message: `In isAccessTokenExpired try block`,
      data: { isAccessTokenExpired, status, resStatus }
    });
    if (
      status === statusCodes.badRequestErrorCode &&
      (resStatus === resStatuses.invalidToken ||
        resStatus === resStatuses.invalidUser)
    ) {
      isAccessTokenExpired = true;
    } else if (
      status === statusCodes.accessForbiddenErrorCode &&
      failureMessage &&
      resStatus === undefined
    ) {
      isAccessTokenExpired = true;
    } else if (
      status === statusCodes.unAuthorizedErrorCode &&
      failureMessage &&
      resStatus === undefined
    ) {
      isAccessTokenExpired = true;
    } else if (
      status === statusCodes.accessForbiddenErrorCode &&
      resStatus === resStatuses.notAuthorizedException
    ) {
      isAccessTokenExpired = true;
    } else if (
      status === statusCodes.notFoundErrorCode &&
      resStatus === resStatuses.userNotFoundException
    ) {
      isAccessTokenExpired = true;
    } else if (
      status === statusCodes.notFoundErrorCode &&
      resStatus === resStatuses.invalidSessionToken
    ) {
      isAccessTokenExpired = true;
    } else if (
      status === statusCodes.unAuthorizedErrorCode &&
      resStatus === resStatuses.invalidCredentials
    ) {
      isAccessTokenExpired = true;
    }
  } catch (e) {
    console.error('error in check error type', e);
    Sentry.captureException(e);
  }
  Sentry.addBreadcrumb({
    type: 'info',
    category: 'api.utils',
    message: `In isAccessTokenExpired`,
    data: { isAccessTokenExpired, errorResponse }
  });
  return {
    isAccessTokenExpired
  };
}

function isRefreshTokenExpiredMessage(message: string) {
  const errorResponse = JSON.parse(message);
  let isRefreshTokenExpired = false;
  try {
    const { status, data } = errorResponse;
    const resStatus = data?.res_status;
    // infrastructure errors cases
    Sentry.addBreadcrumb({
      type: 'info',
      category: 'api.utils',
      message: `In isRefreshTokenExpired try block`,
      data: { isRefreshTokenExpired, status, resStatus }
    });
    if (
      status === statusCodes.badRequestErrorCode &&
      resStatus === resStatuses.refreshTokenExpired
    ) {
      isRefreshTokenExpired = true;
    }
  } catch (e) {
    console.error('error in check error type', e);
    Sentry.captureException(e);
  }
  Sentry.addBreadcrumb({
    type: 'info',
    category: 'api.utils',
    message: `In isRefreshTokenExpired`,
    data: { isRefreshTokenExpired, errorResponse }
  });
  return {
    isRefreshTokenExpired
  };
}

const isIbUserAccountBaseUrl = (url: string) => {
  if (url.includes(Config['IB_USER_ACCOUNT_URL'])) {
    return true;
  }
  return false;
};

const setAPIHeaders = (api: ApisauceInstance, options) => {
  const userDetails = getUserInfoDetails();
  let authorization =
    options && options.getAccessToken
      ? { Authorization: `Bearer ${options.getAccessToken()}` }
      : {};
  if (isIbUserAccountBaseUrl(api.getBaseURL())) {
    api.setHeaders({
      ...authorization,
      'x-browser-session-id': userDetails.browserSessionId
    });
  } else {
    api.setHeaders({
      ...authorization
    });
  }
};

export const networkCallWithApisauceWithHeaders =
  (store: any) =>
  async (
    api: ApisauceInstance,
    url: string,
    requestObject: Record<string, any>,
    type: any = apiMethods.post,
    options = {
      getAccessToken: store.getAccessToken,
      getRefreshToken: store.getRefreshToken
    }
  ) => {
    const { getAccessToken, getRefreshToken } = options;
    setAPIHeaders(api, options);
    let response: any = null;
    try {
      // NOTE: same api is invocation method is used in AuthApiUtils also. for any modifications update the same there
      response = await getData(api, url, requestObject, type, true);
    } catch (error) {
      const { message } = error;

      const { isAccessTokenExpired } = isAccessTokenExpiredMessage(message);
      if (isAccessTokenExpired) {
        // NOTE: To avoid circular dependencies we are passing store as an argument

        const request = {
          refresh_token: getRefreshToken()
        };
        Sentry.setContext(`APIUtils`, {
          authDomain: Config['AUTH_COOKIE_DOMAIN'],
          accessToken: getAccessToken(),
          refreshToken: getRefreshToken()
        });
        Sentry.addBreadcrumb({
          type: 'info',
          category: 'api.utils',
          message: `APIUtils before Refresh Token request`,
          data: {
            authDomain: Config['AUTH_COOKIE_DOMAIN'],
            accessToken: getAccessToken(),
            refreshToken: getRefreshToken()
          }
        });
        try {
          const refreshTokensResponse =
            await store.refreshAuthTokensAPI(request);
          if (refreshTokensResponse?.access_token === '')
            throw Error(
              JSON.stringify({
                res_status: 'SESSION_EXPIRED',
                response: 'Session Expired!',
                http_status_code: 400
              })
            );
        } catch (error) {
          throw error;
        }

        setAPIHeaders(api, options);

        Sentry.setContext(`APIUtils Refresh Token Success`, {
          authDomain: Config['AUTH_COOKIE_DOMAIN'],
          accessToken: getAccessToken(),
          refreshToken: getRefreshToken()
        });
        Sentry.addBreadcrumb({
          type: 'info',
          category: 'api.utils',
          message: `APIUtils Refresh Token Success`,
          data: {
            authDomain: Config['AUTH_COOKIE_DOMAIN'],
            accessToken: getAccessToken(),
            refreshToken: getRefreshToken()
          }
        });
        //@ts-ignore
        response = await getData(api, url, requestObject, type, true);
      } else {
        throw error;
      }
    }
    return response;
  };

export const networkCallWithApisauce = async (
  api: ApisauceInstance,
  url: string,
  requestObject: Record<string, any>,
  type: any,
  options?: {
    getAccessToken: () => string;
  }
  //@ts-ignore
): Promise<T> => {
  let isConnected = false;
  try {
    isConnected = await Promise.resolve(navigator.onLine);
  } catch (e) {
    return Promise.reject(new Error(JSON.stringify({})));
  }

  setAPIHeaders(api, options);

  if (isConnected) {
    let response = null;
    try {
      //@ts-ignore
      response = await getData(api, url, requestObject, type, true);
    } catch (error) {
      const { message } = error;

      const { isRefreshTokenExpired } = isRefreshTokenExpiredMessage(message);
      if (isRefreshTokenExpired) {
        throw Error(
          JSON.stringify({
            res_status: 'REFRESH_TOKEN_EXPIRED',
            response: 'Refresh Token Expired!',
            http_status_code: 400
          })
        );
      } else {
        throw error;
      }
    }
    return response;
  } else {
    throw new Error(JSON.stringify({ http_status_code: 503 }));
  }
};

export function isAPISuccess(...args) {
  const status = true;
  return Array.from(args).reduce(
    (returnStatus, item) => returnStatus && parseInt(item, 10) === API_SUCCESS,
    status
  );
}

export function isAPIFailed(...args) {
  return Array.from(args).indexOf(API_FAILED) !== -1;
}

/**
    Takes only network call status of multiple calls and
    returns true if any one of them is in loading condition
  */

export function isAPIFetching(...args) {
  const status = false;
  return Array.from(args).reduce(
    (returnStatus, item) => returnStatus || parseInt(item, 10) === API_FETCHING,
    status
  );
}

export function isAPIInitial(...args) {
  const status = false;
  const initialStatus = Array.from(args).reduce(
    (returnStatus, item) => returnStatus || parseInt(item, 10) === API_INITIAL,
    status
  );
  return initialStatus;
}

export function getLoadingStatus(...args) {
  if (isAPISuccess(...args)) {
    return API_SUCCESS;
  } else if (isAPIFailed(...args)) {
    return API_FAILED;
  } else if (isAPIFetching(...args)) {
    return API_FETCHING;
  } else if (isAPIInitial(...args)) {
    return API_INITIAL;
  }
  return API_SUCCESS;
}

const getGraphQLData = (
  api: ApisauceInstance,
  requestBody: Record<string, any>
) => {
  return api.post('', requestBody);
};

export const networkCallWithGraphQL =
  (refreshAuthTokensAPI, getAccessToken, getRefreshToken) =>
  async (
    api: ApisauceInstance,
    requestObject: Record<string, any>,
    type: string = apiMethods.post
  ) => {
    let isConnected = false;

    try {
      isConnected = await Promise.resolve(navigator.onLine);
    } catch (e) {
      return Promise.reject(new Error(JSON.stringify({})));
    }

    if (isConnected) {
      api.setHeaders({
        Authorization: `Bearer ${getAccessToken()}`
      });

      let response = await getGraphQLData(api, requestObject);

      if (!response.data) {
        throw new Error(JSON.stringify(response));
      }
      const { errors } = response.data as any;

      if (errors && errors[0]) {
        const { extensions } = errors[0];
        const resStatus = extensions?.['res_status'] ?? '';

        if (
          resStatus === 'AUTHENTICATION_ERROR' ||
          resStatus === 'AUTHENTICATION_CREDS_NOT_PROVIDED'
        ) {
          const request = {
            refresh_token: getRefreshToken()
          };
          Sentry.setContext(`APIUtils`, {
            authDomain: Config['AUTH_COOKIE_DOMAIN'],
            accessToken: getAccessToken(),
            refreshToken: getRefreshToken()
          });
          Sentry.addBreadcrumb({
            type: 'info',
            category: 'api.utils',
            message: `APIUtils before Refresh Token request`,
            data: {
              authDomain: Config['AUTH_COOKIE_DOMAIN'],
              accessToken: getAccessToken(),
              refreshToken: getRefreshToken()
            }
          });

          const refreshTokensResponse = await refreshAuthTokensAPI(request);
          if (refreshTokensResponse?.access_token === '')
            throw Error(
              JSON.stringify({
                res_status: 'SESSION_EXPIRED',
                response: 'Session Expired!',
                http_status_code: 400
              })
            );
          api.setHeaders({
            Authorization: `Bearer ${getAccessToken()}`
          });
          Sentry.setContext(`APIUtils Refresh Token Success`, {
            authDomain: Config['AUTH_COOKIE_DOMAIN'],
            accessToken: getAccessToken(),
            refreshToken: getRefreshToken()
          });
          Sentry.addBreadcrumb({
            type: 'info',
            category: 'api.utils',
            message: `APIUtils Refresh Token Success`,
            data: {
              authDomain: Config['AUTH_COOKIE_DOMAIN'],
              accessToken: getAccessToken(),
              refreshToken: getRefreshToken()
            }
          });

          response = await getGraphQLData(api, requestObject);

          if (!response.ok || !response.data) {
            throw new Error(JSON.stringify(response));
          }

          return response.data.data;
        } else throw new Error(JSON.stringify(response));
      }

      return response.data.data;
    } else {
      throw new Error(JSON.stringify({ http_status_code: 503 }));
    }
  };
